import type { ViewForSiderFieldsFragment, ViewGroupFieldsFragment } from '@salescore/client-api'
import type { DataNode as AntdNode } from 'antd/es/tree'
import { Array, Filter, Order, pipe } from 'effect'

export type ExpandedAntdNodeSerializable = Omit<AntdNode, 'children'> & {
  searchKey: string
  children: ExpandedAntdNodeSerializable[]
  item: ViewForSiderFieldsFragment | ViewGroupFieldsFragment
}

type ViewGroupTree = ViewGroupFieldsFragment & {
  children: ViewGroupTree[]
}

interface DfsResult {
  node: ViewGroupTree
  parentIndex: number
  parentNodes: ViewGroupTree[]
}

class ViewSearcher {
  // private readonly views: CoreView[]
  private readonly mapper: Record<string, ViewForSiderFieldsFragment[]>

  // private readonly views: ViewForSiderFieldsFragment[]

  public constructor(views: ViewForSiderFieldsFragment[]) {
    // this.views = views
    this.mapper = Array.groupBy(views, (x) => x.viewGroupId ?? '') // 空文字のとき、親グループなし
  }

  public searchViewsByViewGroupId(viewGroupId: string): ViewForSiderFieldsFragment[] {
    return this.mapper[viewGroupId] ?? []
  }
}

export function generateViewGroupTreeKey(viewGroupId: string) {
  return [`g`, viewGroupId].join('_')
}

export function convertFlatViewGroupsToAntdTree(
  viewGroups: ViewGroupFieldsFragment[],
  views: ViewForSiderFieldsFragment[],
): ExpandedAntdNodeSerializable[] {
  // uniqueは必要ないが、念の為やっておく
  const viewGroupTrees = createViewGroupTree(Array.dedupeWith(viewGroups, (a, b) => a.id === b.id)) // まずViewGroupsのツリーを作る
  const searcher = new ViewSearcher(Array.dedupeWith(views, (a, b) => a.id === b.id))
  return [
    // viewGroupによるツリー
    // ViewGroupsのツリーに対してviewsを付与しつつ、antdのツリー構造に変換
    ...pipe(
      viewGroupTrees,
      Array.sortBy(Order.mapInput(Order.Number, (x) => x.rank)),
      Array.map((x) => convertViewGroupTreeToAntdTree(x, searcher)),
    ),
    // viewによるツリー（ルートしかないツリー、すなわち単体ノード）
    ...pipe(
      views,
      Array.filter((view) => view.viewGroupId === undefined || view.viewGroupId === null),
      Array.sortBy(Order.mapInput(Order.Number, (x) => x.rank)),
      Array.map((view) => viewToNode(view)),
    ),
  ]
}

function createViewGroupTree(viewGroups: ViewGroupFieldsFragment[]): ViewGroupTree[] {
  // ルートとそれ以外に分割してから再帰する
  const [roots, children] = pipe(
    viewGroups,
    Array.map((x): ViewGroupTree => ({
      ...x,
      children: [],
    })),
    Array.partition(Filter.fromPredicate((x) => x.viewGroupId === undefined || x.viewGroupId === null)),
  )
  return createViewGroupTreeRec(roots, children)
}

function convertViewGroupTreeToAntdTree(
  viewGroupTree: ViewGroupTree,
  searcher: ViewSearcher,
): ExpandedAntdNodeSerializable {
  const views = searcher.searchViewsByViewGroupId(viewGroupTree.id)
  return {
    item: {
      ...viewGroupTree,
      // Omitしているが、Viewの__typenameが紛れ込むことがあるので明示的に指定する
      __typename: 'ViewGroup',
    },
    key: generateViewGroupTreeKey(viewGroupTree.id),
    searchKey: viewGroupTree.name,
    children: [
      ...pipe(
        viewGroupTree.children,
        Array.sortBy(Order.mapInput(Order.Number, (x) => x.rank)),
        Array.map((child) => convertViewGroupTreeToAntdTree(child, searcher)),
      ),
      ...pipe(
        views,
        Array.sortBy(Order.mapInput(Order.Number, (x) => x.rank)),
        Array.map((view) => viewToNode(view)),
      ),
    ],
  }
}

function viewToNode(view: ViewForSiderFieldsFragment): ExpandedAntdNodeSerializable {
  return {
    item: {
      ...view,
      // Omitしているが、Viewの__typenameが紛れ込むことがあるので明示的に指定する
      __typename: 'ViewForSider',
    },
    key: view.id,
    searchKey: view.name,
    isLeaf: true,
    children: [],
  }
}

function createViewGroupTreeRec(rootNodes: ViewGroupTree[], candidateNodes: ViewGroupTree[]): ViewGroupTree[] {
  if (candidateNodes.length === 0) {
    return rootNodes
  }

  const restNodes = []
  for (const candidateNode of candidateNodes) {
    const parentId = candidateNode.viewGroupId
    if (parentId === undefined || parentId === null) {
      continue
    }

    const parentNode = dfsForViewGroupTree(rootNodes, parentId)?.node
    if (parentNode === undefined) {
      restNodes.push(candidateNode)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      parentNode.children ??= []
      parentNode.children.push(candidateNode)
    }
  }
  if (candidateNodes.length === restNodes.length) {
    return rootNodes
  }
  return createViewGroupTreeRec(rootNodes, restNodes)
}

function dfsForViewGroupTree(nodes: ViewGroupTree[], id: string | number): DfsResult | undefined {
  // 全ての子ツリーについて探索
  // eslint-disable-next-line no-plusplus
  for (let index = 0; index < nodes.length; index++) {
    // 対象のキーだったらcallbackを発火
    // eslint-disable-next-line sonarjs/different-types-comparison
    if (nodes[index]?.id === id) {
      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        node: nodes[index]!,
        parentIndex: index,
        parentNodes: nodes,
      }
    }
    const children = nodes[index]?.children

    // 深さ優先探索
    if (children === undefined) {
      continue
    }

    const result = dfsForViewGroupTree(children, id)
    if (result !== undefined) {
      return result
    }
  }
  return undefined
}
